"use client";

import { useState } from "react";

function Separator() {
  return (
    <span aria-hidden className="text-foreground/25">
      |
    </span>
  );
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function ArticleMeta({
  date,
  updatedDate,
  author,
  readTime,
  url,
  title,
}: {
  date: string;
  updatedDate?: string;
  author?: string;
  readTime: number;
  url: string;
  title: string;
}) {
  const [copied, setCopied] = useState(false);

  const formattedDate = formatDate(date);
  // Only surface an update when it is genuinely later than publication, so a
  // same-day tweak doesn't render a confusing "Updated" line.
  const showUpdated = Boolean(
    updatedDate && new Date(updatedDate) > new Date(date),
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently ignore
    }
  };

  return (
    <div className="text-text-secondary mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm leading-6">
      {author && (
        <>
          <span>By {author}</span>
          <Separator />
        </>
      )}

      <time dateTime={date}>{formattedDate}</time>

      {showUpdated && updatedDate && (
        <>
          <Separator />
          <span>
            Updated <time dateTime={updatedDate}>{formatDate(updatedDate)}</time>
          </span>
        </>
      )}

      <Separator />

      <span>{readTime} min read</span>

      <Separator />

      {/* Kept in its own wrapper so flex-wrap breaks between the meta groups
          rather than inside the share links. */}
      <span className="flex items-center gap-1.5">
        <a
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}
          target="_blank"
          rel="noopener"
          className="hover:text-foreground no-underline transition-colors"
        >
          X
        </a>
        <span aria-hidden>·</span>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
          target="_blank"
          rel="noopener"
          className="hover:text-foreground no-underline transition-colors"
        >
          Facebook
        </a>
        <span aria-hidden>·</span>
        <button
          type="button"
          onClick={copyLink}
          className="hover:text-foreground cursor-pointer transition-colors"
        >
          {copied ? "Copied!" : "Copy link"}
        </button>
      </span>
    </div>
  );
}
